import React, { useCallback, useMemo } from 'react';
import createCachedSelector from 're-reselect';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';

import { HooksDisplayGrid } from 'ui-components/components/hooks-display-grid';

import ShipmentsGridPlaceholder from './grid-placeholder';
import { RefundAssistDisabledPlaceholder } from './grid-placeholder/refund-assist-disabled-placeholder';
import { useSelectedShipmentData } from './hooks';
import { useSelectedShippingViewId } from './shipping-left-nav/shipping-left-nav-context';
import GridLoading from '../../components/grid-loading';
import ShippingGridHotkeys from '../../components/hotkeys/advanced-hotkeys/shipping-grid-hotkeys';
import { baseColumns, REFUND_ASSIST_COLUMNS, VOIDED_GRID_COLUMNS, VOIDED_GRID_COLUMN_IDS } from '../../components/shipping-grid/columns';
import { REFUND_ASSIST_VIEW_ID, VOIDED_VIEW_ID } from '../../components/shipping-grid/views/view-id-constants';
import { emptyArray } from 'ui-components/default-props/empty';
import { useShouldShowVoidType } from '../../hooks/refund-assist/use-should-show-void-type';
import { useBTEmailExperienceNew, useIsRefundAssistEnabled } from '../../hooks/use-seller-setting';
import Global from '../../store/global-selectors';
import { shipmentGridChangeColumns, shipmentGridChangeOrderBy } from '../../store/shipments/grid/shipments.grid.actions';
import { setSelectedShipments } from '../../store/shipments/selected-shipments/shipments.selected-shipments.actions';
import { goToShipmentDetailsForFulfillmentId } from '../../store/shipments/shipment-details/shipments.details.actions';
import { saveUserSettingGridConfig } from '../../store/user/user.actions';

const columnSelector = createCachedSelector(
  (column) => column,
  (_, baseColumn) => baseColumn.render,
  (_, baseColumn) => baseColumn.getMessageDescriptor,
  (_, baseColumn) => baseColumn.id,
  (column, render, getMessageDescriptor, id) => {
    const name = <FormattedMessage {...getMessageDescriptor()} />;
    return {
      ...column,
      render,
      name,
      id,
    };
  },
)((column) => column.id);

const mapConfigurationSelector = createSelector(
  (columnConfiguration) => columnConfiguration,
  (_, statusViewId) => statusViewId,
  (_, __, shouldShowVoidTypeColumn) => shouldShowVoidTypeColumn,
  (columnConfiguration, statusViewId, shouldShowVoidTypeColumn) => {
    let availableColumns = baseColumns;

    // Add refund assist specific columns only for the refund assist view
    if (statusViewId === REFUND_ASSIST_VIEW_ID) {
      availableColumns = [...baseColumns, ...REFUND_ASSIST_COLUMNS];
    }

    if (statusViewId === VOIDED_VIEW_ID && shouldShowVoidTypeColumn) {
      availableColumns = [...baseColumns, ...VOIDED_GRID_COLUMNS];
    }

    // Filter out void-type column from user config if they shouldn't see it
    let filteredConfiguration = columnConfiguration;
    if (!shouldShowVoidTypeColumn) {
      filteredConfiguration = columnConfiguration.filter((column) => !VOIDED_GRID_COLUMN_IDS.includes(column.id));
    }

    return filteredConfiguration
      .map((column) => {
        const baseColumn = availableColumns.find((c) => c.id === column.id);
        return baseColumn && columnSelector(column, baseColumn);
      })
      .filter((column) => !!column);
  },
);

const getColumnId = (column) => column.id;

const ShippingGridContainer = (props) => {
  const dispatch = useDispatch();
  const { selectedFulfillmentIds, selectedFulfillmentPlanIds, selectedSalesOrderIds } = useSelectedShipmentData();
  const isLoading = useSelector(Global.selectShipmentsGridIsLoading);
  const activeFilterViewId = useSelector(Global.selectShipmentsFilteringActiveFilterViewId);
  const statusViewId = useSelectedShippingViewId();
  const enableNewEmailExperience = useBTEmailExperienceNew();
  const isRefundAssistEnabled = useIsRefundAssistEnabled();
  const columnConfiguration = useSelector((state) =>
    Global.selectShipmentsGridCurrentViewGridConfigOrDefault(state, {
      statusViewId,
      activeFilterViewId,
      enableNewEmailExperience,
    }),
  );
  const currentPageFulfillmentPlanIds = useSelector(Global.selectShipmentsGridCurrentPageFulfillmentIds);
  const shouldShowVoidTypeColumn = useShouldShowVoidType();
  const gridData = useMemo(
    () =>
      currentPageFulfillmentPlanIds.map((fulfillmentId) => ({
        id: fulfillmentId,
      })),
    [currentPageFulfillmentPlanIds],
  );
  const columns = mapConfigurationSelector(columnConfiguration, statusViewId, shouldShowVoidTypeColumn);
  const onColumnsChanged = useCallback(
    (newColumns) => {
      dispatch(saveUserSettingGridConfig('shipments', statusViewId, activeFilterViewId, newColumns));
      dispatch(shipmentGridChangeColumns(statusViewId, activeFilterViewId, newColumns));
    },
    [dispatch, statusViewId, activeFilterViewId],
  );
  const onOrderByChanged = useCallback(
    (newColumns) => {
      dispatch(saveUserSettingGridConfig('shipments', statusViewId, activeFilterViewId, newColumns));
      dispatch(shipmentGridChangeOrderBy(statusViewId, activeFilterViewId, newColumns));
    },
    [dispatch, statusViewId, activeFilterViewId],
  );
  const setSelectedFulfillmentIds = useCallback(
    (selectedIds) => {
      dispatch(setSelectedShipments(selectedIds));
    },
    [dispatch],
  );
  const onRowDoubleClick = useCallback(
    (shipmentDetails) => {
      const { id } = shipmentDetails;
      dispatch(goToShipmentDetailsForFulfillmentId(id));
    },
    [dispatch],
  );

  const getPlaceholder = useCallback(() => {
    if (isLoading) {
      return <GridLoading />;
    }

    if (!isRefundAssistEnabled && statusViewId === REFUND_ASSIST_VIEW_ID) {
      return <RefundAssistDisabledPlaceholder />;
    }

    return <ShipmentsGridPlaceholder statusViewId={statusViewId} />;
  }, [isLoading, isRefundAssistEnabled, statusViewId]);

  return (
    <ShippingGridHotkeys
      selectedFulfillmentIds={selectedFulfillmentIds}
      selectedFulfillmentPlanIds={selectedFulfillmentPlanIds}
      selectedSalesOrderIds={selectedSalesOrderIds}
    >
      <HooksDisplayGrid
        {...props}
        columns={columns}
        data={isLoading ? emptyArray : gridData}
        getColumnId={getColumnId}
        onColumnsChanged={onColumnsChanged}
        onOrderByChanged={onOrderByChanged}
        onRowDoubleClick={onRowDoubleClick}
        onSelectedChanged={setSelectedFulfillmentIds}
        placeholder={getPlaceholder()}
        selected={selectedFulfillmentIds}
        shouldForceShowPlaceholder={!isRefundAssistEnabled && statusViewId === REFUND_ASSIST_VIEW_ID}
      />
    </ShippingGridHotkeys>
  );
};

export default ShippingGridContainer;
